// §6.4 / §9.7 — the shell's global key handler.
//
// It runs in the capture phase on `document`, so a built-in cannot swallow a reserved chord
// before the shell sees it. An external app in an iframe is a different matter entirely — the
// frame gets `keydown` first and the parent never sees it, which is why §9.7 makes the app
// forward reserved chords back over the bridge (M8.12). Both paths land in `dispatch` here.
import type { Binding, Keymap } from '@hangar/shared';
import {
  canonicalChord,
  chordFromEvent,
  detectPlatform,
  isBrowserReserved,
  parseModifierTap,
  type HostPlatform,
  type KeyEventLike,
} from './chords';
import { isShellAction, type ShellAction } from './actions';

export * from './chords';
export * from './actions';

export interface KeyboardOptions {
  platform?: HostPlatform;
  /** Runs the action. Supplied by the composition root, which is what owns wm/launch/shellui. */
  dispatch: (action: ShellAction) => void;
  /** Called the first time the user presses a chord the browser has taken (§6.4). */
  onBrowserReserved?: (chord: string) => void;
  /** True while the shell owns the whole keyboard, which reclaims the stolen chords. */
  immersive?: () => boolean;
  /**
   * §9.7 step 4 — the claimed set changed, and every bridged app has to be told.
   *
   * Called only when the set is genuinely different, and driven by the *keymap* rather than by
   * the skin setting: a per-binding override (§11.6) changes what the shell claims just as a
   * skin does, and the setting fires before the new keymap has been loaded.
   */
  onReservedChanged?: (chords: string[]) => void;
}

export interface KeyboardService {
  readonly platform: HostPlatform;
  setKeymap(keymap: Keymap): void;
  /** §11.6 per-binding overrides: action id → chord. */
  setOverrides(overrides: Record<string, string>): void;
  /** Canonical chords the shell claims, sent to every app at handshake (§9.7). */
  reservedChords(): string[];
  bindingFor(chord: string): Binding | undefined;
  /** The chord currently bound to an action, after overrides. */
  chordFor(action: string): string | undefined;
  /**
   * §11.6 — every binding the active keymap declares, in its own order, with overrides applied.
   *
   * Settings needs the *list* rather than a lookup: "view the active keymap" is a page, and a page
   * that could only ask about actions it already knew would go stale the moment a skin added one.
   */
  bindings(): readonly Binding[];
  /** The keymap exactly as the skin wrote it — what "Reset to default" on a row goes back to. */
  declared(): readonly Binding[];
  /** Runs the action bound to a chord. Returns true when something fired. */
  dispatchChord(chord: string): boolean;
  /** Handles a key event. Returns true when the shell consumed it. */
  handle(event: KeyEventLike): boolean;
  /**
   * §6.4 — the release half of a **modifier tap** (`Meta` on the Windows skin). Returns true when
   * the tap fired: the modifier went down and came back up with nothing pressed in between.
   */
  handleUp(event: KeyEventLike): boolean;
  /** Attach the capture-phase listener. Returns a detach function. */
  attach(target: Pick<Document, 'addEventListener' | 'removeEventListener'>): () => void;
}

export function createKeyboardService(options: KeyboardOptions): KeyboardService {
  const platform =
    options.platform ??
    (typeof navigator === 'undefined'
      ? 'other'
      : detectPlatform(navigator.userAgent, navigator.platform));

  let keymap: Keymap | null = null;
  let overrides: Record<string, string> = {};
  let byChord = new Map<string, Binding>();
  /** Bare-modifier bindings, keyed by the `KeyboardEvent.key` the modifier reports (D-103). */
  let byTap = new Map<string, Binding>();
  /** The canonical tap armed by the last keydown, or null once anything else happened. */
  let armedTap: string | null = null;
  const warnedBrowserChords = new Set<string>();
  /** The set last announced, so an unchanged rebuild is not re-sent to every app (§9.7). */
  let announced: string | null = null;

  const reserved = (): string[] =>
    [...byChord.values(), ...byTap.values()].filter((b) => b.reserved === true).map((b) => b.chord);

  const rebuild = (): void => {
    const next = new Map<string, Binding>();
    const taps = new Map<string, Binding>();
    for (const binding of keymap?.bindings ?? []) {
      const raw = overrides[binding.id] ?? binding.chord;
      const tap = parseModifierTap(raw);
      if (tap) {
        taps.set(tap.eventKey, { ...binding, chord: tap.canonical });
        continue;
      }
      const chord = canonicalChord(raw);
      if (chord === null) continue;
      next.set(chord, { ...binding, chord });
    }
    byChord = next;
    byTap = taps;
    armedTap = null;

    const chords = reserved();
    const key = chords.join(' ');
    if (key === announced) return;
    announced = key;
    options.onReservedChanged?.(chords);
  };

  /** The keymap's own order, so the page reads the way the skin wrote it. */
  const listBindings = (): Binding[] =>
    (keymap?.bindings ?? []).map((binding) => {
      const raw = overrides[binding.id] ?? binding.chord;
      const tap = parseModifierTap(raw);
      return { ...binding, chord: tap ? tap.canonical : (canonicalChord(raw) ?? binding.chord) };
    });

  const bindingFor = (chord: string): Binding | undefined => {
    const tap = parseModifierTap(chord);
    if (tap) return byTap.get(tap.eventKey);
    const canonical = canonicalChord(chord);
    return canonical === null ? undefined : byChord.get(canonical);
  };

  const dispatchChord = (chord: string): boolean => {
    const binding = bindingFor(chord);
    if (!binding || !isShellAction(binding.id)) return false;
    options.dispatch(binding.id);
    return true;
  };

  const handle = (event: KeyEventLike): boolean => {
    // A tap is armed by its own modifier going down alone, and disarmed by literally anything
    // else — which is what makes `Meta` open Start but `Meta+E` open Explorer.
    if (byTap.size > 0) {
      const tap = byTap.get(event.key);
      armedTap = tap && !event.ctrlKey && !event.altKey && !event.shiftKey ? tap.chord : null;
    }

    const chord = chordFromEvent(event);
    if (chord === null) return false;

    if (byChord.has(chord)) return dispatchChord(chord);

    // Nothing is bound — but if the browser just took a chord a desktop would want, say so
    // once and never again (§6.4's binding copy lives in `copy.ts`).
    if (isBrowserReserved(chord) && !(options.immersive?.() ?? false)) {
      if (!warnedBrowserChords.has(chord)) {
        warnedBrowserChords.add(chord);
        options.onBrowserReserved?.(chord);
      }
    }
    return false;
  };

  const service: KeyboardService = {
    platform,

    setKeymap(next) {
      keymap = next;
      rebuild();
    },

    setOverrides(next) {
      overrides = { ...next };
      rebuild();
    },

    reservedChords: reserved,

    bindingFor,

    bindings: listBindings,

    declared: () => (keymap?.bindings ?? []).map((binding) => ({ ...binding })),

    chordFor(action) {
      return [...byChord.values(), ...byTap.values()].find((b) => b.id === action)?.chord;
    },

    dispatchChord,
    handle,

    handleUp(event) {
      const armed = armedTap;
      armedTap = null;
      if (armed === null) return false;
      const tap = byTap.get(event.key);
      if (!tap || tap.chord !== armed) return false;
      return dispatchChord(tap.chord);
    },

    attach(target) {
      type DomKeyEvent = KeyEventLike & { preventDefault(): void; stopPropagation(): void };
      const onDown = (event: Event): void => {
        const key = event as unknown as DomKeyEvent;
        if (handle(key)) {
          key.preventDefault();
          key.stopPropagation();
        }
      };
      // §6.4's modifier tap is the only thing the shell reads on the way *up* (D-103).
      const onUp = (event: Event): void => {
        const key = event as unknown as DomKeyEvent;
        if (service.handleUp(key)) {
          key.preventDefault();
          key.stopPropagation();
        }
      };
      target.addEventListener('keydown', onDown, true);
      target.addEventListener('keyup', onUp, true);
      return () => {
        target.removeEventListener('keydown', onDown, true);
        target.removeEventListener('keyup', onUp, true);
      };
    },
  };

  return service;
}
